import React, { useState } from "react";
import AuthService from "../service/AmplifyAuth";
import StringUtils from "../utils/StringUtils";
import ErrorUtils from "../utils/ErrorUtils";
import { Button, Card, Field } from "../components/UI";
import { useAppContext } from "../context/AppContext";
import { useNavigate } from "../router";

export default function ChangePassword({ passwordUser = null, onCancel }) {
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword1: "", newPassword2: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { commit, addToast } = useAppContext();
  const navigate = useNavigate();

  const clearFieldError = (field, value, nextForm = passwordForm) => {
    setFieldErrors((errors) => {
      if (!errors[field]) return errors;
      let shouldClear = !StringUtils.isEmpty(value);

      if (field === "newPassword1" || field === "newPassword2") {
        const password = field === "newPassword1" ? value : nextForm.newPassword1;
        const confirmation = field === "newPassword2" ? value : nextForm.newPassword2;
        shouldClear = Boolean(password && confirmation && password === confirmation);
      }

      if (!shouldClear) return errors;
      const nextErrors = { ...errors };
      delete nextErrors[field];
      if (field === "newPassword1" || field === "newPassword2") {
        delete nextErrors.newPassword1;
        delete nextErrors.newPassword2;
      }
      return nextErrors;
    });
  };

  const updateField = (field, value) => {
    setPasswordForm((form) => {
      const nextForm = { ...form, [field]: value };
      clearFieldError(field, value, nextForm);
      return nextForm;
    });
  };

  const validatePassword = () => {
    const errors = {};
    if (!passwordUser && StringUtils.isEmpty(passwordForm.currentPassword)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Current Password is required", life: 5000 });
      errors.currentPassword = "Current Password is required";
    }
    if (StringUtils.isEmpty(passwordForm.newPassword1)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "New Password is required", life: 5000 });
      errors.newPassword1 = "New Password is required";
    }
    if (StringUtils.isEmpty(passwordForm.newPassword2)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "New Password ReEntry is required", life: 5000 });
      errors.newPassword2 = "New Password ReEntry is required";
    }
    if (passwordForm.newPassword1 && passwordForm.newPassword2 && passwordForm.newPassword1 !== passwordForm.newPassword2) {
      addToast({ severity: "error", summary: "Validation Error", detail: "New Password and New Password ReEntry do not match", life: 5000 });
      errors.newPassword2 = "New Password and New Password ReEntry do not match";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const updatePassword = async () => {
    if (isSubmitting) return;
    if (!validatePassword()) return;

    try {
      setIsSubmitting(true);
      if (passwordUser) {
        const user = await AuthService.completeNewPassword(passwordUser, passwordForm.newPassword1);
        commit("signInUser", user);
        navigate("/account");
      } else {
        const user = await AuthService.currentAuthenticatedUser();
        await AuthService.changePassword(user, passwordForm.currentPassword, passwordForm.newPassword1);
        navigate("/account");
      }
      addToast({ severity: "success", summary: "Success", detail: "Password Update Successful", life: 5000 });
    } catch (error) {
      const msg = ErrorUtils.parseError(error);
      addToast({ severity: "error", summary: "Error", detail: msg, life: 10000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancel = () => {
    if (isSubmitting) return;
    addToast({
      severity: "info",
      summary: "Info",
      detail: passwordUser ? "Required Password update cancelled" : "Password update cancelled",
      life: 5000,
    });
    if (passwordUser && onCancel) {
      onCancel();
    } else {
      navigate("/account");
    }
  };

  const onEnter = (event) => {
    if (event.key === "Enter") {
      updatePassword();
    }
  };

  const isAuthFlow = Boolean(passwordUser);
  const card = (
      <Card className={isAuthFlow ? "auth-modern-card auth-modern-card-narrow" : "account-form-card"}>
        {isAuthFlow ? (
          <h4 className="auth-modern-card-title">Update Password</h4>
        ) : (
          <div className="account-form-head">
            <p className="dashboard-section-kicker">Security</p>
            <h2>Update Password</h2>
          </div>
        )}
        {isAuthFlow ? <p className="auth-modern-card-copy">Create a new password before continuing to your host portal.</p> : null}
        {!passwordUser ? (
          <Field label="Current password" required className={isAuthFlow ? "auth-modern-field" : ""} error={fieldErrors.currentPassword}>
            <input type="password" className="form-control" placeholder="Current password" value={passwordForm.currentPassword} autoComplete="current-password" onKeyDown={onEnter} onChange={(event) => updateField("currentPassword", event.target.value)} />
          </Field>
        ) : null}
        <Field label="New password" required className={isAuthFlow ? "auth-modern-field" : ""} error={fieldErrors.newPassword1}>
          <input type="password" className="form-control" placeholder="New password" value={passwordForm.newPassword1} autoComplete="new-password" onKeyDown={onEnter} onChange={(event) => updateField("newPassword1", event.target.value)} />
        </Field>
        <Field label="Re-enter new password" required className={isAuthFlow ? "auth-modern-field" : ""} error={fieldErrors.newPassword2}>
          <input type="password" className="form-control" placeholder="Re-enter new password" value={passwordForm.newPassword2} autoComplete="new-password" onKeyDown={onEnter} onChange={(event) => updateField("newPassword2", event.target.value)} />
        </Field>
        <div className={isAuthFlow ? "auth-modern-actions-row" : "account-form-actions"}>
          {!isAuthFlow ? <Button label="Cancel" className="btn-danger" disabled={isSubmitting} onClick={cancel} /> : null}
          <Button label={isSubmitting ? "Updating..." : "Update"} className={isAuthFlow ? "auth-modern-primary" : ""} disabled={isSubmitting} onClick={updatePassword} />
          {isAuthFlow ? <Button label="Cancel" className="auth-modern-secondary" disabled={isSubmitting} onClick={cancel} /> : null}
        </div>
      </Card>
  );

  if (isAuthFlow) return card;

  return (
    <div className="account-page-modern account-form-page">
      {card}
    </div>
  );
}
